import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { Confirmation } from '@abp/ng.theme.shared';
import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ActionMenuComponent } from 'src/app/shared/components/action-menu/action-menu.component';
import { ActionMenuItem } from 'src/app/shared/components/action-menu/models/action-menu-item.model';
import { BaseListComponent } from 'src/app/shared/components/base-list/base-list.component';
import { BaseListColumn } from 'src/app/shared/components/base-list/models/base-list-column.model';
import { BaseListConfig } from 'src/app/shared/components/base-list/models/base-list-config.model';
import { ListRequestDto } from 'src/core/models/request/list-request.dto';
import { ListResponseDto } from 'src/core/models/request/list-response.dto';
import { ConfirmationService } from 'src/core/services/confirmation/confirmation.service';
import { CrudService } from 'src/core/services/crud/crud.service';
import { ToasterService } from 'src/core/services/toaster/toaster.service';
import { WrapUpDefinitionDto } from 'src/core/models/wrap-up-definition/wrap-up-definition.dto';
import { localizePredefinedWrapUpCodeName } from 'src/core/helpers/wrap-up-code-name.helper';
import { WrapUpOutcomePolicy } from 'src/core/models/wrap-up-definition/wrap-up-outcome-policy.enum';
import { BaseListColumnTemplateContext } from 'src/app/shared/components/base-list/models/base-list-column-template-context.model';

const QUEUES_INLINE_LIMIT = 3;

type WrapUpTemplateContext = BaseListColumnTemplateContext<WrapUpDefinitionDto>;

@Component({
  selector: 'app-wrap-up-definition-list',
  standalone: true,
  imports: [
    CommonModule,
    LocalizationPipe,
    ActionMenuComponent,
    BaseListComponent,
    TagModule,
    TooltipModule,
  ],
  templateUrl: './wrap-up-definition-list.component.html',
})
export class WrapUpDefinitionListComponent implements AfterViewInit {
  private readonly localization = inject(LocalizationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly crudService = inject(CrudService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly toasterService = inject(ToasterService);

  @ViewChild('nameTpl') nameTpl!: TemplateRef<WrapUpTemplateContext>;
  @ViewChild('descriptionTpl') descriptionTpl!: TemplateRef<WrapUpTemplateContext>;
  @ViewChild('policyTpl') policyTpl!: TemplateRef<WrapUpTemplateContext>;
  @ViewChild('queuesTpl') queuesTpl!: TemplateRef<WrapUpTemplateContext>;
  @ViewChild('actionsTpl') actionsTpl!: TemplateRef<WrapUpTemplateContext>;

  @Input() reloadVersion = 0;

  @Output() edit = new EventEmitter<WrapUpDefinitionDto>();
  @Output() deleted = new EventEmitter<void>();

  codes: WrapUpDefinitionDto[] = [];
  loading = false;
  listConfig!: BaseListConfig<WrapUpDefinitionDto>;

  ngOnInit(): void {
    this.loadList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reloadVersion'] && !changes['reloadVersion'].firstChange) {
      this.loadList();
    }
  }

  ngAfterViewInit(): void {
    const l = (key: string) => this.localization.instant(key);

    const columns: BaseListColumn<WrapUpDefinitionDto>[] = [
      {
        field: 'name',
        header: l('Settings::WorkEnvironment:WrapupCodes:Name'),
        bodyTemplate: this.nameTpl,
      },
      {
        field: 'description',
        header: l('Settings::WorkEnvironment:WrapupCodes:Description'),
        sortable: false,
        bodyTemplate: this.descriptionTpl,
      },
      {
        field: 'policy',
        header: l('Settings::WorkEnvironment:WrapupCodes:Policy'),
        width: '160px',
        bodyTemplate: this.policyTpl,
      },
      {
        field: 'queues',
        header: l('Settings::Queues:Title'),
        width: '250px',
        bodyTemplate: this.queuesTpl,
      },
      {
        field: 'id',
        header: l('Settings::WorkEnvironment:WrapupCodes:Actions'),
        width: '120px',
        bodyTemplate: this.actionsTpl,
      },
    ];

    this.listConfig = {
      columns,
      trackByField: 'id',
      emptyMessage: l('Settings::WorkEnvironment:WrapupCodes:EmptyMessage'),
    };

    this.cdr.detectChanges();
  }

  getPolicyLabel(code: WrapUpDefinitionDto): string {
    const key =
      code.policy === WrapUpOutcomePolicy.Reschedule
        ? 'Settings::WorkEnvironment:WrapupCodes:PolicyReschedule'
        : 'Settings::WorkEnvironment:WrapupCodes:PolicyCompleted';

    return this.localization.instant(key);
  }

  isTruncated(element: HTMLElement): boolean {
    return element.scrollWidth > element.clientWidth;
  }

  getQueuesInline(code: WrapUpDefinitionDto): { id: number; name: string }[] {
    return code.queues?.slice(0, QUEUES_INLINE_LIMIT) ?? [];
  }

  hasQueuesOverflow(code: WrapUpDefinitionDto): boolean {
    return (code.queues?.length ?? 0) > QUEUES_INLINE_LIMIT;
  }

  getQueuesOverflowCount(code: WrapUpDefinitionDto): number {
    return Math.max(0, (code.queues?.length ?? 0) - QUEUES_INLINE_LIMIT);
  }

  getQueuesOverflowTooltip(code: WrapUpDefinitionDto): string {
    return (code.queues ?? [])
      .slice(QUEUES_INLINE_LIMIT)
      .map(q => q.name)
      .join('\n');
  }

  getDisplayName(code: WrapUpDefinitionDto): string {
    return code.isPredefined
      ? localizePredefinedWrapUpCodeName(this.localization, code.name)
      : code.name;
  }

  getMenuItems(row: WrapUpDefinitionDto): ActionMenuItem[] {
    if (row.isPredefined) {
      return [
        {
          label: this.localization.instant('Settings::WorkEnvironment:WrapupCodes:AssignQueues'),
          icon: 'pi pi-pencil',
          action: () => this.edit.emit(row),
        },
      ];
    }

    return [
      {
        label: this.localization.instant('Settings::WorkEnvironment:WrapupCodes:Edit'),
        icon: 'pi pi-pencil',
        action: () => this.edit.emit(row),
      },
      {
        label: this.localization.instant('Settings::WorkEnvironment:WrapupCodes:Delete'),
        icon: 'pi pi-trash',
        severity: 'danger',
        action: () => this.onDeleteClick(row),
      },
    ];
  }

  onDeleteClick(row: WrapUpDefinitionDto): void {
    this.confirmationService
      .warn(
        this.localization.instant(
          'Settings::WorkEnvironment:WrapupCodes:DeleteConfirmMessage',
          row.name,
        ),
        'Settings::WorkEnvironment:WrapupCodes:DeleteConfirmTitle',
        {
          hideCancelBtn: false,
          hideYesBtn: false,
          dismissible: true,
          yesText: 'Settings::WorkEnvironment:WrapupCodes:Delete',
          cancelText: 'AbpUi::Cancel',
        },
      )
      .pipe(
        switchMap(status => {
          if (status === Confirmation.Status.confirm) {
            return this.crudService.delete(WrapUpDefinitionDto, row.id).pipe(map(() => true));
          }

          return of(false);
        }),
      )
      .subscribe({
        next: isDeleted => {
          if (!isDeleted) {
            return;
          }

          this.toasterService.success('::SuccessfullyDeleted');
          this.deleted.emit();
        },
      });
  }

  private loadList(): void {
    this.loading = true;

    const request: ListRequestDto = {
      skipCount: 0,
      maxResultCount: 1000,
      filters: [],
      sorters: [],
    };

    this.crudService.get<WrapUpDefinitionDto>(WrapUpDefinitionDto, request).subscribe({
      next: (response: ListResponseDto<WrapUpDefinitionDto>) => {
        this.codes = response.items ?? [];
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }
}
